import React from 'react';
import type { FC } from 'react';
import { Spinner, useAsync, useAsyncCallback } from '@wix/cli-ui-kit';
import { I18nProvider } from '@wix/cli-i18n';
import { useBiLogger } from '@wix/cli-telemetry-react';
import {
  wixCliLoginStart,
  wixCliLoginEnd,
} from '@wix/bi-logger-dev-tools-data/v2';
import { match } from 'variant';
import type { DeviceCodeResponse } from '../schemas.js';
import messages from '../messages.json' with { type: 'json' };
import { unifiedAuthStrategy } from '../auth-strategies/index.js';
import type {
  AuthStrategy,
  AuthStrategyState,
} from '../auth-strategies/index.js';
import { LoginInstructions } from './LoginInstructions.js';
import { client } from '../client.js';

interface Props {
  loginFn: AuthStrategy['login'];
  onLogin: (data: AuthStrategyState) => void;
  onError: (error: unknown) => void;
}

export const Login: FC<Props> = ({ loginFn, onLogin, onError }) => {
  const biLogger = useBiLogger();

  function handleError(error: unknown) {
    void biLogger.report(
      wixCliLoginEnd({
        isSuccess: false,
        ...(error instanceof Error
          ? {
              errorType: error.name,
            }
          : {
              errorType: 'unknown',
              errorMessage: JSON.stringify(error),
            }),
      })
    );

    onError(error);
  }

  const loginAction = useAsyncCallback(
    async ({ signal }, deviceCodeResponse: DeviceCodeResponse) => {
      try {
        const authData = await loginFn({
          deviceCode: deviceCodeResponse,
          signal,
        });
        void biLogger.report(
          wixCliLoginEnd({
            isSuccess: true,
          })
        );
        onLogin(authData);
      } catch (error) {
        handleError(error);
      }
    }
  );

  const { status } = useAsync(
    async () => {
      void biLogger.report(wixCliLoginStart({}));

      return client.requestDeviceCode();
    },
    [],
    {
      onSuccess: (deviceCodeResponse) => {
        void loginAction.execute(deviceCodeResponse);
      },
      onError: handleError,
    }
  );

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner />,
    Success: ({ result: { userCode, verificationUri } }) => (
      <LoginInstructions
        userCode={userCode}
        verificationUri={verificationUri}
      />
    ),
  });
};

export function LoginWithI18n(props: Omit<Props, 'loginFn'>) {
  return (
    <I18nProvider messages={messages}>
      <Login loginFn={unifiedAuthStrategy.login} {...props} />
    </I18nProvider>
  );
}
